import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "無料で使える背景透過サイトおすすめ5選【2026年最新比較】",
	description:
		"無料で使える背景透過サイトを2026年最新で比較。登録不要・制限なし・商用OKなど用途別に5つのサービスをランキング形式で紹介。EC出品・SNS・デザインに使えるおすすめを解説。",
	publishDate: "2026-03-24",
	slug: "background-transparent-site-free-top5-2026",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function BackgroundTransparentSiteFree2026() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<section>
						<p className="text-gray-700 mb-6">
							「背景透過サイトはたくさんあるけど、どれが一番いいの？」という疑問にお答えします。
							無料・登録不要・商用OK・精度——それぞれ何を重視するかによっておすすめが変わります。
							この記事では2026年現在で使えるおすすめ5サービスを比較し、用途別に選び方を解説します。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							背景透過サイトを選ぶ5つのポイント
						</h2>
						<div className="bg-gray-50 rounded-xl p-5 mb-6 text-sm text-gray-700">
							<ul className="space-y-2">
								<li>
									<span className="font-semibold">① 無料枚数：</span>
									1日・1ヶ月に何枚まで無料か
								</li>
								<li>
									<span className="font-semibold">② 登録不要か：</span>
									アカウント作成・ログインが不要かどうか
								</li>
								<li>
									<span className="font-semibold">③ 出力解像度：</span>
									無料版でも高解像度で保存できるか
								</li>
								<li>
									<span className="font-semibold">④ 商用利用：</span>
									EC出品・ビジネス用途で使えるか
								</li>
								<li>
									<span className="font-semibold">⑤ プライバシー：</span>
									画像がサーバーに保存・使用されるか
								</li>
							</ul>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							おすすめ背景透過サイト5選（2026年版）
						</h2>

						<div className="space-y-6">
							<div className="border-2 border-indigo-200 rounded-xl p-5">
								<div className="flex items-center gap-2 mb-2">
									<span className="bg-indigo-700 text-white text-xs px-2 py-1 rounded-full font-bold">
										第1位
									</span>
									<h3 className="font-bold text-indigo-700">
										しろぬきや — 登録不要・無料・EC対応
									</h3>
								</div>
								<p className="text-sm text-gray-700 mb-3">
									ブラウザで開いてすぐ使える背景透過サイト。アカウント登録もログインも不要。
									AIが自動で背景を検出・除去し、透明PNG（または白背景）でダウンロードできます。
									画像データはサーバーに送信されないため、商品写真などのプライバシーも安心。
									さらに、Amazon・楽天・メルカリなどEC規格サイズへの自動リサイズも対応しており、
									出品作業をそのまま完結できます。
								</p>
								<div className="bg-indigo-50 rounded-lg p-3 text-sm">
									<ul className="space-y-1 text-gray-700">
										<li>✅ 登録不要・ログイン不要</li>
										<li>✅ 無料で使用可能</li>
										<li>✅ 高解像度出力対応</li>
										<li>✅ 商用利用OK</li>
										<li>✅ 白背景・ECサイズリサイズ対応</li>
										<li>✅ 画像がサーバーに保存されない</li>
									</ul>
								</div>
							</div>

							<div className="border rounded-xl p-5">
								<div className="flex items-center gap-2 mb-2">
									<span className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full font-bold">
										第2位
									</span>
									<h3 className="font-bold text-gray-700">
										remove.bg — 業界最高水準の精度
									</h3>
								</div>
								<p className="text-sm text-gray-700 mb-3">
									背景除去ツールの代名詞的存在。AIの精度は業界トップクラスで、複雑な髪の毛・細かい輪郭も高精度に処理します。
									ただし無料版は月50枚・低解像度（約625×400px）に制限されており、EC出品には有料版（月$9〜）が必要になることも。
								</p>
								<div className="bg-gray-50 rounded-lg p-3 text-sm">
									<ul className="space-y-1 text-gray-700">
										<li>✅ 業界最高水準の精度</li>
										<li>⚠️ 無料版：月50枚・低解像度</li>
										<li>⚠️ アカウント登録が必要</li>
										<li>⚠️ 商用利用は有料プランのみ</li>
									</ul>
								</div>
							</div>

							<div className="border rounded-xl p-5">
								<div className="flex items-center gap-2 mb-2">
									<span className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full font-bold">
										第3位
									</span>
									<h3 className="font-bold text-gray-700">
										Adobe Express — 高精度・無料枠あり
									</h3>
								</div>
								<p className="text-sm text-gray-700 mb-3">
									Adobeが提供する無料のデザインツールに含まれる背景除去機能。髪の毛など細かい部分の処理が得意。
									Adobe
									IDが必要ですが、無料プランでも一定枚数使用できます。切り抜き後にAdobe
									Expressでデザイン編集もできます。
								</p>
								<div className="bg-gray-50 rounded-lg p-3 text-sm">
									<ul className="space-y-1 text-gray-700">
										<li>✅ 高精度エッジ処理</li>
										<li>✅ 切り抜き後のデザイン編集も可能</li>
										<li>⚠️ Adobe IDが必要</li>
										<li>⚠️ 無料版には枚数制限あり</li>
									</ul>
								</div>
							</div>

							<div className="border rounded-xl p-5">
								<div className="flex items-center gap-2 mb-2">
									<span className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full font-bold">
										第4位
									</span>
									<h3 className="font-bold text-gray-700">
										Canva 背景リムーバー — デザイン込みで使いたい場合
									</h3>
								</div>
								<p className="text-sm text-gray-700 mb-3">
									Canvaのデザインツールに搭載された背景除去機能。背景を消した後にそのままバナーやSNS投稿のデザインができるのが強み。
									Canvaアカウントが必要で、枚数には制限があります。大量の商品写真処理には向いていません。
								</p>
								<div className="bg-gray-50 rounded-lg p-3 text-sm">
									<ul className="space-y-1 text-gray-700">
										<li>✅ 切り抜き後にデザイン編集が得意</li>
										<li>✅ 無料枠あり</li>
										<li>⚠️ Canvaアカウントが必要</li>
										<li>⚠️ 大量処理には不向き</li>
									</ul>
								</div>
							</div>

							<div className="border rounded-xl p-5">
								<div className="flex items-center gap-2 mb-2">
									<span className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full font-bold">
										第5位
									</span>
									<h3 className="font-bold text-gray-700">
										PhotoRoom — スマホで使いたい場合
									</h3>
								</div>
								<p className="text-sm text-gray-700 mb-3">
									スマートフォンアプリとして人気の背景除去ツール。撮影からそのまま切り抜きまでスマホで完結できます。
									無料版はウォーターマーク（透かし）が入るため、商用利用には有料プランへの移行が必要です。
								</p>
								<div className="bg-gray-50 rounded-lg p-3 text-sm">
									<ul className="space-y-1 text-gray-700">
										<li>✅ スマホアプリが充実</li>
										<li>✅ 高精度エッジ処理</li>
										<li>⚠️ 無料版はウォーターマーク付き</li>
										<li>⚠️ 商用利用は有料プランが必要</li>
									</ul>
								</div>
							</div>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">比較まとめ表</h2>
						<div className="overflow-x-auto mb-6">
							<table className="w-full text-sm border-collapse">
								<thead>
									<tr className="bg-gray-100">
										<th className="border border-gray-200 px-3 py-2 text-left">
											サービス
										</th>
										<th className="border border-gray-200 px-3 py-2 text-center">
											登録不要
										</th>
										<th className="border border-gray-200 px-3 py-2 text-center">
											無料枠
										</th>
										<th className="border border-gray-200 px-3 py-2 text-center">
											高解像度
										</th>
										<th className="border border-gray-200 px-3 py-2 text-center">
											商用OK
										</th>
									</tr>
								</thead>
								<tbody>
									<tr className="bg-indigo-50">
										<td className="border border-gray-200 px-3 py-2 font-semibold text-indigo-700">
											しろぬきや
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											✅
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											✅
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											✅
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											✅
										</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-3 py-2">
											remove.bg
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											❌
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											⚠️月50枚
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											⚠️有料のみ
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											⚠️有料のみ
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-3 py-2">
											Adobe Express
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											❌
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											✅
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											✅
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											⚠️プランによる
										</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-3 py-2">Canva</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											❌
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											✅
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											✅
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											⚠️プランによる
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-3 py-2">
											PhotoRoom
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											❌
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											⚠️透かし付き
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											✅
										</td>
										<td className="border border-gray-200 px-3 py-2 text-center">
											⚠️有料のみ
										</td>
									</tr>
								</tbody>
							</table>
						</div>
						<p className="text-sm text-gray-500">
							※
							2026年3月時点の情報です。各サービスのプラン内容は変更される場合があります。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							今すぐ無料で試す
						</h2>
						<p className="text-gray-700 mb-4">
							登録不要・無料で今すぐ背景透過できます。商品写真・プロフィール画像・ロゴなど、あらゆる画像の背景をAIが自動で除去。
							EC出品に必要な白背景への変換・規格サイズへのリサイズまでまとめて対応しています。
						</p>

						<CtaBlock />
					</section>

					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
